import React, { useEffect, useState } from "react";
import "../styles/home.css";
import "../styles/table.css";
import "../styles/theme.css";
import Layout from "./Layout";
import { getDispensedPresetData, runCheckGrossTotals, runCheckTransIds } from "../functions/api/transactions";
import TransactionTable from "./display/TableComponent";
import DropdownComponent from "./display/DropDownComponent";
import { getSiteNames } from "../functions/api/details";
import { DispDataEntry } from "../interfaces/interfaces";
import SmallTableComponent from "./display/SmallTable";
import DescriptionList from "./display/DetailsComponent";


const getYesterday = (): string => {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    return date.toISOString().slice(0, 10); // YYYY-MM-DD
};


const DispTransComponent: React.FC = () => {
    // Gets the Dispenser Preset Analysis Data
    const [dispPresetData, setDispPresetData] = useState<any[]>([]);
    const [selectedFromDate, setSelectedFromDate] = useState<string>(getYesterday());
    const [code, setSelectedCode] = useState<string>('Lusaka');
    const [dispTransData, setDispTransData] = useState<any[]>([]);

    // Loading display - TODO: Replace with spinner
    const [loading, setLoading] = useState<boolean>(true);
    const [siteOptions, setSiteOptions] = useState<any[]>([]);
    const [missingTransData, setMissingTransData] = useState<any[]>([]);
    const [grossTotalData, setGrossTotalData] = useState<any[]>([]);


    const populateSiteItems = async () => {
        // Fetching the site names
        const data = await getSiteNames();

        if (Array.isArray(data)) {
            setSiteOptions(data);
        } else {
            setSiteOptions([]);
        }

    };


    const loadDispenserTransactions = async (code: string, fromDate: string) => {
        setLoading(true);
        const data = await getDispensedPresetData(code, fromDate);
        if (Array.isArray(data)) {
            setDispPresetData(data);
        } else {
            setDispPresetData([]);
        }
        setLoading(false);
    };


    const loadDispTransIdCheck = async (dispData: DispDataEntry[]) => {
        setLoading(true);
        const data = await runCheckTransIds(dispData);
        if (Array.isArray(data)) {
            setDispTransData(data);
        } else {
            setDispTransData([]);
        }
        setLoading(false);
    };


    useEffect(() => {
        populateSiteItems();
        loadDispenserTransactions(code, selectedFromDate);
    }, []);


    const handleFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newDate = e.target.value;
        setSelectedFromDate(newDate);

        // Clear previous analysis data
        setMissingTransData([]);
        setGrossTotalData([]);

        loadDispenserTransactions(code, newDate);
    };


    const handleDropdownChange = (newCode: string) => {
        setSelectedCode(newCode);

        // Clear previous analysis data

        setMissingTransData([]);
        setGrossTotalData([]);

        loadDispenserTransactions(newCode, selectedFromDate);
    };


    return (
        <Layout>
            <div className="home-container" style={{ overflowX: "auto" }}>
                <h2 className="home-header">Dispenser Preset Analysis</h2>
                <DescriptionList text="
                                This report shows transactions per site.
                                /nYou can select the from date to choose transactions that occurred from until now.
                                /nYou can filter on exceeded preset to see cases where dispensing overshot the preset amount, total anomaly where a totaliser anomaly is detected, and error code.
                                /n* The Analyse Missing Items button shows transactions that were not sent by the device on site.
                                /n* The Analyse Gross Total Jumps button shows where the totaliser does not match what was expected in contiguous transactions.
                                " 
                />
                
                {/* Receive Loads Section */}
                <div>

                    <div className="dropdown">
                        <DropdownComponent
                            options={siteOptions}
                            value={code}
                            onChange={handleDropdownChange}
                            label="Choose code"
                        />
                        {code && <p className="small">You selected: {code}</p>} {/*} Added this code to stay consistent with the other pages */}
                    </div>

                    {/* Date Selection */}
                    <div style={{ marginBottom: "1rem" }}>
                        <label style={{ marginRight: "0.5rem" }}>From:</label>
                        <div className="date-picker-wrapper">

                            {/* Left Arrow Button */}
                            <button className="date-arrow" onClick={() => {
                                const prev = new Date(selectedFromDate);
                                prev.setDate(prev.getDate() - 1);
                                handleFromDateChange({
                                    target: { value: prev.toISOString().slice(0, 10) }
                                } as React.ChangeEvent<HTMLInputElement>);
                            }}
                            >←
                            </button>

                            <input
                                type="date"
                                value={selectedFromDate}
                                onChange={handleFromDateChange}
                                style={{ padding: "0.4rem" }}
                            />

                            {/* Right Arrow Button */}
                            <button className="date-arrow" onClick={() => {
                                const next = new Date(selectedFromDate);
                                next.setDate(next.getDate() + 1);
                                handleFromDateChange({
                                    target: { value: next.toISOString().slice(0, 10) }
                                } as React.ChangeEvent<HTMLInputElement>);
                            }}
                            >→
                            </button>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <p>Loading...</p>
                ) : dispPresetData.length > 0 ? (
                    <div>
                        <div className="table-scroll-wrapper">
                        <TransactionTable data={dispPresetData} />
                        </div>

                        {/* Analyse Trans Id's Continuity */}

                        <button className="button" onClick={async () => {
                            const analysedData = await runCheckTransIds(dispPresetData);
                            setMissingTransData(Array.isArray(analysedData['data']) ? analysedData['data'] : []);
                        }}
                        >
                            Analyse Missing Items
                        </button>


                        {missingTransData.length > 0 && (
                            <div className="normal-heading" style={{ marginTop: "2rem" }}>
                                <h3>Missing Transactions</h3>
                                <div className="table-scroll-wrapper">
                                    <SmallTableComponent data={missingTransData} />
                                </div>
                            </div>
                        )}

                        {/* Analyse Totaliser Continuity */}

                        <button className="button" onClick={async () => {
                            const analysedData = await runCheckGrossTotals(dispPresetData);
                            setGrossTotalData(Array.isArray(analysedData['data']) ? analysedData['data'] : []);
                        }}
                        >
                            Analyse Gross Total Jumps
                        </button>

                        {grossTotalData.length > 0 && (
                            <div className="normal-heading" style={{ marginTop: "2rem" }}>
                                <h3>Gross Total Jumps</h3>
                                <div className="table-scroll-wrapper">
                                    <SmallTableComponent data={grossTotalData} />
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <p>No Dispenser preset found for {selectedFromDate}.</p>
                )}
            </div>
        </Layout>
    );
};

export default DispTransComponent;
